import React, { FC, ReactElement } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { NavigateFunction } from 'react-router';
import { clsx } from 'clsx';
// Components
import Text from '../../../components/text';
import Space from '../../../components/layout/space';
import EditReservationWrapper from './editReservationWrapper';
import Loader from '../../../components/loader';
import Separator from '../../../components/layout/separator';
// Hooks
import { useCancelRide, useGetCancelReasons } from '../../../core/services/rides';
// Resources
import { stringsRes } from '../../../resources/strings';
import { CancelRideInput, CancelRideOutput, GetCancelReasonsOuput, PickerOption, Ride } from '../../../core/apiTypes';
import { PropsFromModal } from '../../../components/modals/createModalHOC';
import RadioButtonGroup from '../../../components/form/radioGroup';
import { ButtonType } from '../../../components/modals/templates/modalButton';

interface CancelReservationProps extends PropsFromModal {
    ride: Ride;
    navigate: NavigateFunction;
    onConfirmAction?: (data: any, reasonLabel: string) => void;
}

const CancelReservation: FC = (props: CancelReservationProps): ReactElement => {
    const { ride, onRequestClose, onConfirmAction } = props;

    const classes = useStyles();

    //= ======================================//
    //= ============== Queries ===============//
    //= ======================================//

    const cancelRide = useCancelRide();

    const { data: reasons, isFetching } = useGetCancelReasons<GetCancelReasonsOuput>();

    //========================================//
    //=============== UseState ===============//
    //========================================//
    const [selectedReason, setSelectedReason] = React.useState<string>(null);
    const [showError, setShowError] = React.useState<boolean>(false);

    //= ======================================//
    //= ============== Actions ===============//
    //= ======================================//

    const onChangeReason = (e: React.ChangeEvent<HTMLInputElement>) => {
        // console.log('radio checked', e.target.value);
        setSelectedReason(e.target.value);
        setShowError(false);
    };

    const onCancel = (): void => {
        onRequestClose?.();
    };

    const onConfirm = (): void => {
        if (selectedReason == null) {
            setShowError(true);
            return;
        }

        const payload: CancelRideInput = {
            id: ride.id,
            cancel_reason_id: selectedReason,
        };

        console.log(payload);
        // onRequestClose?.();
        // onConfirmAction?.(payload, reasons[ride.status].find((r) => r.id === selectedReason)?.label);

        cancelRide.mutate(payload, {
            onSuccess: (data: CancelRideOutput) => {
                onRequestClose?.();
                onConfirmAction?.(data, reasons[ride.status].find((r) => r.id === selectedReason)?.label);
            },
        });
    };

    //= ======================================//
    //= ============== Renders ===============//
    //= ======================================//

    const renderCancelButtons = (): JSX.Element => {
        const radioOptions: PickerOption[] =
            reasons[ride.status]?.map(({ id, label }) => ({
                value: `${id}`,
                label,
            })) || [];

        return (
            <RadioButtonGroup
                title=""
                color="secondary"
                name="selector"
                labelPlacement="start"
                options={radioOptions}
                value={selectedReason}
                onChange={onChangeReason}
            />
        );
    };

    return (
        <EditReservationWrapper
            {...props}
            onConfirm={onConfirm}
            onCancel={onCancel}
            loadingConfirm={cancelRide.isLoading}
            // disableConfirm={selectedReason == null}
            typeConfirm={ButtonType.ERROR}
            labelConfirm={stringsRes.common.cancelRide}
            labelCancel={stringsRes.common.back}
            buttonsClassName={clsx(classes.buttonsContainer)}
            noSpacing
        >
            <Space value={1} />
            <Text.Description14 text={stringsRes.booking.cancelRideFees} />
            <Text.Description14
                text={stringsRes.booking.cancelRideReason}
                customColor={showError ? 'red' : 'default'}
            />
            <Space value={2} />

            {isFetching && <Loader />}
            {!isFetching && (
                <>
                    <div className={classes.tipsContainer}>
                        <div className={classes.tipsBtnContainer}>{renderCancelButtons()}</div>
                    </div>
                </>
            )}
            <Separator height={1} />
            <Space value={2} />

            {/* <Space value={1} />
            <TextButton
                customColor="lightblue"
                title={stringsRes.tripHistory.reportIncident}
                onClick={onGoToSupport}
                className={classes.link}
            /> */}
        </EditReservationWrapper>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        link: {
            fontSize: 14,
        },
        tipsBtnContainer: {
            display: 'flex',
            flex: '100%',
            width: '100%',
            flexDirection: 'row',
            alignItems: 'center',
            overflowX: 'auto',
            gap: 16,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
            '& > div': {
                width: '100%',
            },
            '& > div > div': {
                flex: 1,
            },
            '& > div > div > label': {
                justifyContent: 'space-between',
                borderTop: `1px solid ${theme.palette.colors.background.separator}`,
                padding: '20px 0px',
                margin: 0,
            },
            '& > div > div > label > .MuiTypography-root': {
                fontSize: 16,
            },
        },
        tipsContainer: {
            gap: 16,
            display: 'flex',
            flexDirection: 'column',
        },
        buttonsContainer: {
            '& > button': {
                width: '100%',
                maxWidth: 'unset !important',
            },
        },
    }),
);

export default CancelReservation;
